import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Typography from '@mui/material/Typography'
import FormHelperText from '@mui/material/FormHelperText'
import TextField from '@mui/material/TextField'
import { SignupOtpInput } from '@/features/auth/components/SignupOtpInput'
import { LockoutCountdown } from '@/features/auth/components/LockoutCountdown'
import { errorInputSx } from '@/features/auth/styles/authFormStyles'
import type { InstrumentLinkOnboardingViewModel } from '../hooks/useInstrumentLinkOnboarding'
import { BackToLoginLink } from './BackToLoginLink'
import {
  ONBOARDING_OTP_HEADING,
  ONBOARDING_OTP_SUBTITLE,
  ONBOARDING_PHONE_HEADING,
  ONBOARDING_PHONE_PLACEHOLDER,
  ONBOARDING_PHONE_SUBTITLE,
} from '../lib/constants'
import type { SpOnboardingChannel } from '../lib/types'
import {
  channelSectionTitleSx,
  instrumentContactFieldSx,
  instrumentContactInputSx,
  instrumentContactLabelSx,
  instrumentOtpTitleBlockSx,
  instrumentPhoneTitleBlockSx,
  instrumentVerifyContentColumnSx,
  instrumentVerifyOtpLabelSx,
  instrumentVerifyPrimaryButtonSx,
  instrumentVerifyResendLinkSx,
  surveyFirstFormColumnSx,
  surveyFirstOtpContentColumnSx,
  surveyFirstTitleBlockSx,
} from '../styles/instrumentOnboardingStyles'
import {
  signupShareContactInputSx,
  signupShareLinkSx,
  signupShareOtpCellSx,
  signupSharePrimaryButtonSx,
  useSignupShareTheme,
} from '../styles/signupShareTheme'
import { VerificationMethodCards } from './VerificationMethodCards'

export function contactLabel(channel: SpOnboardingChannel): string {
  return channel === 'sms' ? 'Mobile Number' : 'Email Address'
}

export function contactPlaceholder(channel: SpOnboardingChannel): string {
  return channel === 'sms' ? ONBOARDING_PHONE_PLACEHOLDER : 'Enter your Email Address'
}

export function verifyHeading(showOtpStep: boolean, channel: SpOnboardingChannel): string {
  if (showOtpStep) return ONBOARDING_OTP_HEADING
  if (channel === 'sms') return ONBOARDING_PHONE_HEADING
  return ONBOARDING_OTP_HEADING
}

function verifyTitleBlockSx(showOtpStep: boolean, channel: SpOnboardingChannel) {
  if (showOtpStep || channel !== 'sms') return instrumentOtpTitleBlockSx
  return instrumentPhoneTitleBlockSx
}

function EmailChannelSubtitle() {
  return (
    <>
      Select how you’d like to receive your
      <br />
      verification code.
    </>
  )
}

export function VerifyStepSubtitle({
  showOtpStep,
  channel,
}: Readonly<{ showOtpStep: boolean; channel: SpOnboardingChannel }>) {
  if (showOtpStep) {
    return <>{ONBOARDING_OTP_SUBTITLE}</>
  }
  if (channel === 'sms') {
    return <>{ONBOARDING_PHONE_SUBTITLE}</>
  }
  return <EmailChannelSubtitle />
}

interface VerifyStepHeaderProps {
  readonly showOtpStep: boolean
  readonly channel: SpOnboardingChannel
  readonly headingSx: object
  readonly subtitleSx: object
  readonly surveyFirst?: boolean
}

export function VerifyStepHeader({
  showOtpStep,
  channel,
  headingSx,
  subtitleSx,
  surveyFirst = false,
}: Readonly<VerifyStepHeaderProps>) {
  return (
    <Box sx={[verifyTitleBlockSx(showOtpStep, channel), surveyFirst ? surveyFirstTitleBlockSx : null]}>
      <Typography component="h1" sx={headingSx}>
        {verifyHeading(showOtpStep, channel)}
      </Typography>
      <Typography component="p" sx={subtitleSx}>
        <VerifyStepSubtitle showOtpStep={showOtpStep} channel={channel} />
      </Typography>
    </Box>
  )
}

interface ContactStepProps {
  readonly vm: InstrumentLinkOnboardingViewModel
  readonly session: NonNullable<InstrumentLinkOnboardingViewModel['session']>
}

function InstrumentLinkVerificationMethodSection({
  session,
  channel,
  setChannel,
  otpSendPending,
}: Readonly<{
  session: NonNullable<InstrumentLinkOnboardingViewModel['session']>
  channel: SpOnboardingChannel
  setChannel: (channel: SpOnboardingChannel) => void
  otpSendPending: boolean
}>) {
  if (!session.channelAvailability?.email || !session.channelAvailability?.sms) {
    return null
  }
  return (
    <>
      <Typography component="p" sx={channelSectionTitleSx}>
        Choose Verification Method
      </Typography>
      <VerificationMethodCards
        channelAvailability={session.channelAvailability}
        selected={channel}
        onSelect={setChannel}
        disabled={otpSendPending}
      />
    </>
  )
}

export function InstrumentLinkContactStep({ vm, session }: Readonly<ContactStepProps>) {
  const signupTheme = useSignupShareTheme()
  const {
    channel,
    setChannel,
    phone,
    handlePhoneChange,
    handlePhoneBlur,
    phoneError,
    isUsPhoneValid,
    otpSend,
    phoneCheck,
    handleSendOtp,
    showOtpOnboarding,
  } = vm

  return (
    <Box sx={vm.surveyFirstPhone ? surveyFirstFormColumnSx : { display: 'contents' }}>
      {showOtpOnboarding ? (
        <InstrumentLinkVerificationMethodSection
          session={session}
          channel={channel}
          setChannel={setChannel}
          otpSendPending={otpSend.isPending}
        />
      ) : null}
      <Box sx={instrumentContactFieldSx}>
        <Typography component="label" htmlFor="instrument-onboarding-contact" sx={instrumentContactLabelSx}>
          {contactLabel(channel)}
        </Typography>
        <TextField
          id="instrument-onboarding-contact"
          fullWidth
          margin="none"
          value={phone}
          onChange={(event) => handlePhoneChange(event.target.value)}
          onBlur={handlePhoneBlur}
          placeholder={contactPlaceholder(channel)}
          error={channel === 'sms' && Boolean(phoneError)}
          inputProps={{
            inputMode: channel === 'sms' ? 'tel' : 'email',
            autoComplete: channel === 'sms' ? 'tel' : 'email',
            'aria-invalid': channel === 'sms' && Boolean(phoneError),
            'aria-describedby':
              channel === 'sms' && phoneError ? 'instrument-onboarding-phone-error' : undefined,
          }}
          sx={[
            instrumentContactInputSx,
            channel === 'sms' && phoneError ? errorInputSx : null,
            signupTheme ? signupShareContactInputSx : null,
          ]}
          data-testid="instrument-onboarding-contact"
        />
        {channel === 'sms' && phoneError ? (
          <FormHelperText
            id="instrument-onboarding-phone-error"
            error
            data-testid="instrument-onboarding-phone-error"
            sx={{ m: 0 }}
          >
            {phoneError}
          </FormHelperText>
        ) : null}
      </Box>
      <Button
        type="button"
        variant="contained"
        fullWidth
        disableElevation
        disabled={
          otpSend.isPending ||
          phoneCheck.isPending ||
          (channel === 'sms' && !isUsPhoneValid) ||
          !session.channelAvailability?.[channel]
        }
        onClick={handleSendOtp}
        data-testid="instrument-onboarding-send-otp"
        sx={[instrumentVerifyPrimaryButtonSx, signupTheme ? signupSharePrimaryButtonSx : null]}
      >
        {otpSend.isPending || phoneCheck.isPending ? (
          <CircularProgress size={24} color="inherit" />
        ) : (
          'Send OTP'
        )}
      </Button>
      <BackToLoginLink />
    </Box>
  )
}

interface OtpStepProps {
  readonly vm: InstrumentLinkOnboardingViewModel
}

export function InstrumentLinkOtpStep({ vm }: Readonly<OtpStepProps>) {
  const signupTheme = useSignupShareTheme()
  const {
    otpCode,
    setOtpCode,
    serverError,
    otpInlineError,
    lockoutUntilMs,
    isLockedOut,
    resendDisabled,
    resendRemaining,
    otpVerify,
    handleVerifyOtp,
    handleResendOtp,
  } = vm

  const otpError = isLockedOut ? null : otpInlineError ?? serverError

  return (
    <Box sx={vm.surveyFirstPhone ? surveyFirstOtpContentColumnSx : instrumentVerifyContentColumnSx}>
      <Typography component="p" sx={instrumentVerifyOtpLabelSx}>
        Enter verification code
      </Typography>

      {lockoutUntilMs ? (
        <LockoutCountdown
          lockedUntilMs={lockoutUntilMs}
          message={
            serverError ?? 'Too many incorrect attempts. Please wait 2 minutes before trying again.'
          }
        />
      ) : null}

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          width: '100%',
        }}
      >
        {otpError ? (
          <Typography
            role="alert"
            data-testid="instrument-onboarding-otp-error"
            sx={{ color: '#ef4444', fontSize: '14px', letterSpacing: '-0.1504px', m: 0 }}
          >
            {otpError}
          </Typography>
        ) : null}

        <SignupOtpInput
          value={otpCode}
          onChange={setOtpCode}
          disabled={isLockedOut || otpVerify.isPending}
          error={Boolean(otpError)}
          id="instrument-onboarding-otp"
          cellSx={signupTheme ? signupShareOtpCellSx : null}
        />
      </Box>

      <Button
        type="button"
        variant="contained"
        fullWidth
        disableElevation
        disabled={otpCode.length !== 6 || isLockedOut || otpVerify.isPending}
        onClick={handleVerifyOtp}
        data-testid="instrument-onboarding-verify-otp"
        sx={[instrumentVerifyPrimaryButtonSx, signupTheme ? signupSharePrimaryButtonSx : null]}
      >
        {otpVerify.isPending ? <CircularProgress size={24} color="inherit" /> : 'Verify'}
      </Button>

      <Button
        type="button"
        variant="text"
        onClick={handleResendOtp}
        disabled={resendDisabled}
        data-testid="instrument-onboarding-resend-otp"
        sx={[instrumentVerifyResendLinkSx, signupTheme ? signupShareLinkSx : null]}
      >
        {resendRemaining > 0 ? `Resend OTP in ${resendRemaining}s` : 'Resend OTP'}
      </Button>
    </Box>
  )
}
